import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  Mail,
  Phone,
  Building2,
  Eye,
  FolderKanban,
  Wallet,
  Loader2,
  UserCheck,
  UserX
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// CLIENTS V1 - Version Tableau Compact
// =============================================

const ClientsPageV1 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    phone: ''
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    withProjects: 0
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/', { params: { role: 'client' } });
      const clientsData = response.data || [];
      setClients(clientsData);
      
      setStats({
        total: clientsData.length,
        active: clientsData.filter(c => c.is_active !== false).length,
        withProjects: clientsData.filter(c => c.projects_count > 0).length
      });
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newClient.email) {
      toast.error('Email requis');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/users/client', newClient);
      toast.success(`Client créé ! Mot de passe: ${response.data.temp_password}`, {
        duration: 10000
      });
      setShowCreateModal(false);
      setNewClient({ email: '', first_name: '', last_name: '', company_name: '', phone: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const filteredClients = clients.filter(c => {
    const search = searchQuery.toLowerCase();
    return (
      c.company_name?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a365d]">Clients</h1>
            <p className="text-[#64748b] text-sm mt-1">
              {stats.total} clients · {stats.active} actifs
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau client
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">Total</p>
              <p className="text-2xl font-bold text-[#1a365d]">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">Actifs</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">Avec projets</p>
              <p className="text-2xl font-bold text-blue-600">{stats.withProjects}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        <Card className="card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead className="text-center">Projets</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#64748b]" />
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-[#64748b]">
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map(client => (
                  <TableRow key={client.id} className="group">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                          {client.first_name?.[0] || client.email?.[0]?.toUpperCase() || 'C'}
                        </div>
                        <div>
                          <p className="font-medium text-[#1a365d]">
                            {client.first_name} {client.last_name}
                          </p>
                          <p className="text-xs text-[#64748b]">{client.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {client.phone && (
                          <p className="text-sm flex items-center gap-1 text-[#64748b]">
                            <Phone className="w-3 h-3" /> {client.phone}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {client.company_name ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Building2 className="w-3 h-3 text-[#64748b]" />
                          {client.company_name}
                        </div>
                      ) : (
                        <span className="text-[#94a3b8]">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-slate-100 text-slate-600">
                        {client.projects_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {client.is_active !== false ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">
                          <UserX className="w-3 h-3 mr-1" />
                          Inactif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                            <Eye className="w-4 h-4 mr-2" /> Voir profil
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/projects?client=${client.id}`)}>
                            <FolderKanban className="w-4 h-4 mr-2" /> Voir projets
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/wallet?user=${client.id}`)}>
                            <Wallet className="w-4 h-4 mr-2" /> Voir wallet
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Mail className="w-4 h-4 mr-2" /> Envoyer email
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nouveau client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prénom</Label>
                    <Input
                      value={newClient.first_name}
                      onChange={(e) => setNewClient({...newClient, first_name: e.target.value})}
                      placeholder="Jean"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom</Label>
                    <Input
                      value={newClient.last_name}
                      onChange={(e) => setNewClient({...newClient, last_name: e.target.value})}
                      placeholder="Dupont"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    placeholder="client@exemple.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Entreprise</Label>
                  <Input
                    value={newClient.company_name}
                    onChange={(e) => setNewClient({...newClient, company_name: e.target.value})}
                    placeholder="Nom de l'entreprise"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                    placeholder="06 00 00 00 00"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={creating} className="btn-primary">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ClientsPageV1;
